import React from 'react';

import { Toggle } from 'components';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import CardContainer from 'components/cards/CardContainer';
import Dropdown from 'components/form/Dropdown';
import { H5, M, S } from 'components/typography';
import { useChargeCodesOptions } from '../charges-invoices/hooks/useChargeCodesOptions';

import styles from './styles.scss';

type BillingReportProps = {
  channelId: string | undefined;
  isReportMarkupEnabled: boolean;
  markupChargeCodeId: string | null;
  updateIsReportMarkupEnabled: (value: boolean) => void;
  updateMarkupChargeCodeId: (value: string | null) => void;
};

export function BillingReport({
  channelId,
  isReportMarkupEnabled,
  markupChargeCodeId,
  updateIsReportMarkupEnabled,
  updateMarkupChargeCodeId,
}: BillingReportProps) {
  const chargeCodeOptions = useChargeCodesOptions(channelId);
  const { t } = useTranslation();

  const handleToggle = async () => {
    if (isReportMarkupEnabled) {
      try {
        await window.Alert.confirm(turnOffConfirmationDialog(t));
        updateIsReportMarkupEnabled(false);
      } catch {
        // User cancelled - do nothing
      }
    } else {
      updateIsReportMarkupEnabled(true);
    }
  };

  const handleChargeCodeChange = (option: { value: string }) => {
    updateMarkupChargeCodeId(option.value);
  };

  return (
    <CardContainer className={styles.PaymentSettings}>
      <div className={styles.SectionHeader}>
        <H5>{t('abp.billingAndPaymentSettings.billingReport.header')}</H5>
        <S mt={3}>
          {t('abp.billingAndPaymentSettings.billingReport.description')}
        </S>
      </div>

      <div className={styles.BillingReportToggleContainer}>
        <Toggle
          value={isReportMarkupEnabled}
          onChange={handleToggle}
          testId="billingReportMarkupToggle"
        />
        <div className={styles.BillingReportToggleDescription}>
          <M mt={2} className={styles.FieldLabel}>
            <label>
              {t('abp.billingAndPaymentSettings.billingReport.toggleLabel')}
            </label>
          </M>
          {isReportMarkupEnabled && (
            <>
              <M mt={2} className={styles.FieldLabel}>
                <S>
                  {t(
                    'abp.billingAndPaymentSettings.billingReport.toggleDescription'
                  )}
                </S>
              </M>
              <div className={`${styles.SectionContainer} mt-2`}>
                <div className={styles.ChargeCodeLabelContainer}>
                  <M className={styles.FieldLabel}>
                    <label>
                      {t(
                        'abp.billingAndPaymentSettings.billingReport.chargeCodeLabel'
                      )}
                    </label>
                  </M>
                </div>
                <div className={styles.ChargeCodeDropdownContainer}>
                  <Dropdown
                    className={styles.ChargeCodeDropdown}
                    value={markupChargeCodeId}
                    items={chargeCodeOptions}
                    onChange={handleChargeCodeChange}
                    placeholder={t(
                      'abp.billingAndPaymentSettings.billingReport.chargeCodePlaceholder'
                    )}
                    testId="markupChargeCodeDropdown"
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </CardContainer>
  );
}

function turnOffConfirmationDialog(t: TFunction<'translation', undefined>) {
  return {
    title: t('abp.billingAndPaymentSettings.billingReport.turnOffConfirmTitle'),
    message: t(
      'abp.billingAndPaymentSettings.billingReport.turnOffConfirmMessage'
    ),
    confirmText: t(
      'abp.billingAndPaymentSettings.billingReport.turnOffConfirmButton'
    ),
    cancelText: t(
      'abp.billingAndPaymentSettings.billingReport.turnOffCancelButton'
    ),
  };
}
